/*
    Componente RedefineSenha é responsável por permitir que o usuário redefina sua senha. Ele contém um formulário com campos para email, telefone, nova senha, confirmação de senha e código de ativação. O componente gerencia o estado dos campos do formulário, valida os dados e envia as informações para a API para redefinir a senha do usuário. Além disso, ele exibe mensagens de feedback ao usuário sobre o status da operação.
    O componente utiliza o hook useState para gerenciar o estado dos campos do formulário e mensagens de feedback. Ele também utiliza o hook useNavigate do react-router-dom para redirecionar o usuário para a página de resultado após a redefinição bem-sucedida da senha.

    O formulário é dividido em duas etapas: a primeira etapa valida o email e telefone do usuário, e a segunda etapa permite que o usuário insira a nova senha e o código de ativação. A validação dos dados é feita através de uma requisição à API, que retorna uma resposta indicando se os dados são válidos ou não. Se os dados forem válidos, os campos de senha e código de ativação são habilitados para edição.

    O componente também exibe mensagens de feedback ao usuário, informando sobre o status da operação, como sucesso, erro ou aviso. As mensagens são exibidas através do componente Mensagem, que recebe o texto e o tipo da mensagem como props.   
*/

import { useState } from "react";
import CampoTexto from "../CampoTexto";
import Botao from "../Botao";
import Mensagem from "../Mensagem";
import { useNavigate } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const RedefineSenha = () => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const navigate = useNavigate(); // Hook para navegação programática entre páginas. Ele é usado para redirecionar o usuário para outra página após uma ação, como o envio bem-sucedido do formulário de redefinição de senha. O navigate é uma função que pode ser chamada com o caminho da página para a qual você deseja redirecionar o usuário. Por exemplo, navigate('/login') redirecionaria o usuário para a página de login após a redefinição de senha ser concluída com sucesso.
    const [email, setEmail] = useState("");
    const [fone, setFone] = useState("");
    const [senha1, setSenha1] = useState('');
    const [senha2, setSenha2] = useState(''); 
    const [codAtivacao, setCodAtivacao] = useState(''); // Estado para controlar a ativação do email
    const [dadosValidados, setDadosValidados] = useState(false); // Estado para controlar se os dados foram validados. Quando o usuário informar email e telefone e clicar no validar e a validação for bem-sucedida, esse estado será atualizado para true, permitindo que os campos de senha e código de ativação sejam editáveis. Caso contrário, permanecerá false, mantendo os campos de senha e código de ativação desabilitados até que a validação seja concluída com sucesso.  
    
    const [mensagem, setMensagem] = useState({ texto: 'Informe seu email e telefone e clique em [Enviar] para redefinir sua senha . Demais campos serão liberados após validação.', tipo: 'aviso' });//Controla o state dos dados do componente Mensagem para exibir feedback ao usuário.Tipo recebe o status da resposta da API para definir o estilo da mensagem (ex: sucesso, erro, aviso) para aplicar estilo conforme o tipo recebido, conforme o componente mensagem .
    const [carregando, setCarregando] = useState(false);//Para controlar o state de carregamento durante a requisição


    /* Critérios para validação da senha
      O .test() é um método das expressões regulares (regex) no JavaScript. Ele verifica se uma string corresponde ao padrão definido e retorna true ou false.

      Exemplo javascript: 
      /padrão/.test('string a testar') // → true ou false
    */
    const criterios = {
        tamanho:    senha1.length >= 6 && senha1.length <= 12,
        maiuscula:  /[A-Z]/.test(senha1),
        minuscula:  /[a-z]/.test(senha1),
        numero:     /[0-9]/.test(senha1),
        semSimbolo: !/[ !@#$%^&*_=+\-]/.test(senha1),
        senhasIguais: senha1 === senha2 && senha2.length > 0 
    };

    /* Object.values(criterios)
        Extrai apenas os valores do objeto criterios, transformando em um array:
        Exemplo: 
        const criterios = {
        tamanho:      true,
        maiuscula:    false,
        minuscula:    true,
        numero:       true,
        semSimbolo:   true,
        senhasIguais: false
        };

        Object.values(criterios) 
        // → [true, false, true, true, true, false]
    */
    const senhaValida = Object.values(criterios).every(Boolean);



    const aoValidar = async (event) => {
        event.preventDefault(); // Impede o comportamento padrão do formulário, que é recarregar a página ao ser submetido. Isso é importante para permitir que a lógica de validação e envio dos dados seja executada sem interrupções.
        setCarregando (true); // Trava o botão cadastrar durante a requisição
        
            // Limpa mensagens anteriores
            setMensagem({ texto: '', tipo: '' });

            // ✅ Gera o token antes de enviar
            if (!executeRecaptcha) {
                setMensagem({ texto: 'reCAPTCHA não carregado.', tipo: 'erro' });
                return;
            }

            // Chama a função executeRecaptcha com o nome da ação 'cadastro' para gerar o token do reCAPTCHA
            const tokenCaptcha = await executeRecaptcha('redefineSenha'); // ← nome da ação


            try {
                // Envia os dados para a API usando fetch 
                // Usa a variável de ambiente que agora está funcionando
                // O React substitui isso em tempo de execução. A variável baseUrl conterá a URL da API definida no arquivo .env
                const endpoint = !dadosValidados ? '/user-reset-senha' : '/user-redefine-senha'; // Define o endpoint com base no estado de validação dos dados. Se os dados ainda não foram validados (dadosValidados é false), o endpoint será '/user-reset-senha', que é usado para validar o email e telefone do usuário. Se os dados já foram validados (dadosValidados é true), o endpoint será '/user-redefine-senha', que é usado para enviar a nova senha e o código de ativação para a API.   
                const url =  `${process.env.REACT_APP_API_URL}${endpoint}`;

                let dadosParaEnvio = {}; // Variável para armazenar os dados que serão enviados para a API. Ela é declarada como let para permitir que seja reatribuída com os dados apropriados com base no estado de validação dos dados (dadosValidados). Dependendo se os dados foram validados ou não, essa variável conterá apenas o email e telefone para validação ou os dados completos para redefinição da senha, incluindo email, telefone, nova senha e código de ativação.
                if(!dadosValidados){
                    // Se os dados ainda não foram validados, envia apenas o email e telefone para validação
                    dadosParaEnvio = {
                        email: email,
                        fone: fone,
                        tokenCaptcha: tokenCaptcha // Adiciona o token do reCAPTCHA ao objeto de dados que será enviado para o backend
                    }

                }else{
                    // Se os dados já foram validados, envia os dados completos para redefinição da senha
                    dadosParaEnvio = {
                        email: email,
                        fone: fone,
                        senha1: senha1,
                        senha2: senha2,
                        codigoAtivacao: codAtivacao,
                        tokenCaptcha: tokenCaptcha // Adiciona o token do reCAPTCHA ao objeto de dados que será enviado para o backend
                        }
                }
                // Envia os dados do usuário para a API
                const resposta = await fetch(url, {  
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(dadosParaEnvio),
              });

                
                if (resposta.ok) {
                    const dadosRetorno = await resposta.json();
                        
                    if (dadosRetorno.status === 'sucesso') {
                        setMensagem({ texto: dadosRetorno.mensagem, tipo: dadosRetorno.status });
                        // Limpa os campos após o envio se for inclusão, caso contrário, mantém os dados para edição
                        
                        console.log('Dados enviados com sucesso:', dadosRetorno);

                        //Passamos o state para a página de resultado usando o navigate do react-router-dom, que permite enviar um objeto de estado junto com a navegação. Assim, a página de resultado pode acessar esse estado para exibir a mensagem apropriada. Só usaremos a página resultado para exibir mensagens de sucesso. Erros serão exibidos na própria página de cadastro para que o usuário possa corrigir sem perder os dados já preenchidos.
                        
                        if (endpoint === '/user-reset-senha') {
                            setDadosValidados(true); // Atualiza o estado para indicar que os dados foram validados com sucesso, permitindo que os campos de senha e código de ativação sejam editáveis. Isso é importante para garantir que o usuário só possa preencher esses campos após a validação bem-sucedida do email e telefone, evitando erros e garantindo uma melhor experiência do usuário.
                            setCarregando (false); // Libera do botão validar após a validação bem-sucedida dos dados iniciais (email e telefone), permitindo que o usuário prossiga para a etapa de redefinição de senha. O botão será liberado para que o usuário possa clicar novamente para enviar os dados completos para redefinir a senha.

                            return; // Retorna para evitar que o código continue executando e redirecione para a página de resultado antes da validação dos dados. Isso garante que o usuário permaneça na mesma página para corrigir os dados caso a validação falhe, em vez de ser redirecionado para a página de resultado com uma mensagem de erro.

                        }
                        
                        navigate('/resultado', { 
                            state: { 
                                status: dadosRetorno.status,
                                mensagem: dadosRetorno.mensagem
                            }
                            });
                    }else {
                        setMensagem({ texto: dadosRetorno.mensagem, tipo: dadosRetorno.status });
                        setCarregando (false); // Destrava o botão validar após o processamento da requisição
                    return;
                    } 

                }else{
                    /// Aqui o PHP enviou um erro proposital (ex: 400 Bad Request)
                    const erroRetorno = await resposta.json(); 
                    setMensagem({ 
                                texto: erroRetorno.mensagem || "Erro ao processar a solicitação.", 
                                tipo: erroRetorno.status || 'erro'});
                    setCarregando (false); // Destrava o botão cadastrar após o processamento da requisição
                }

            }catch (error) {
                setMensagem({ texto: error.message, tipo: 'erro' });
                setCarregando (false); // Destrava o botão cadastrar após o processamento da requisição
            }

    }

    return (
        <div>
            <div className="container mt-5">
                <h1 className="h1">Redefinir Senha</h1>
                
                <form id="form" onSubmit={aoValidar}>
                    <div className="row mb-3"> 
                    <CampoTexto 
                        label="Email"
                        labelClass="form-label fs-5"
                        className="form-control form-control-lg fs-3"
                        wrapperClass="col-12 col-md-6"
                        tipo="email"
                        placeholder="Digite seu email"
                        obrigatorio={true}
                        editavel={!dadosValidados} // Torna o campo de email somente leitura após as validações iniciais
                        tamanhoMaximo={256}
                        nome="email"
                        id="email"
                        /*O valor do campo será controlado pelo estado email */
                        valor={email} 
                        /*Quando o usuário digitar algo, a função setEmail será chamada para atualizar o estado*/
                        aoDigitado={valor => setEmail(valor)} 
                    />
                    </div>

                    <div className="row mb-3"> 
                    <CampoTexto 
                        label="Fone"
                        labelClass="form-label fs-5"
                        className="form-control form-control-lg fs-3"
                        wrapperClass="col-12 col-md-6"
                        tipo="text"
                        placeholder="Digite seu fone"
                        obrigatorio={true}
                        editavel={!dadosValidados} // Torna o campo de telefone somente leitura após as validações iniciais
                        tamanhoMaximo={11}
                        nome="fone"
                        id="fone"
                        /*O valor do campo será controlado pelo estado fone */
                        valor={fone} 
                        /*Quando o usuário digitar algo, a função setFone será chamada para atualizar o estado*/
                        aoDigitado={valor => setFone(valor)} 
                    />
                    </div>

                    

                    <div className="row mb-3">
                    <CampoTexto 
                      label="Senha"
                      labelClass="form-label fs-5"
                      className="form-control form-control-lg fs-3" 
                      wrapperClass="col-12 col-md-6"
                      tipo="password"
                      placeholder="Digite a nova senha"
                      obrigatorio={dadosValidados} // O campo de senha é obrigatório após a validação bem-sucedida dos dados iniciais (email e telefone). Antes disso, o campo de senha não é obrigatório porque o usuário ainda está na etapa de validação do email e telefone. Após a validação ser bem-sucedida, o campo de senha se torna obrigatório para garantir que o usuário forneça uma nova senha para a conta.
                      editavel={dadosValidados} // torna o campo de senha editável apenas após a validação bem-sucedida dos dados iniciais (email e telefone)
                      tamanhoMaximo={20}
                      nome="senha1"
                      id="senha1"
                      valor={senha1} 
                      aoDigitado={valor => setSenha1(valor)}
                    />
                  </div>

                  <div className="row mb-3">
                    <CampoTexto 
                      label="Confirme a Senha"
                      labelClass="form-label fs-5"
                      className="form-control form-control-lg  fs-3"
                      wrapperClass="col-12 col-md-6"
                      tipo="password"
                      placeholder="Digite a nova senha novamente"
                      obrigatorio={dadosValidados} // O campo de senha é obrigatório após a validação bem-sucedida dos dados iniciais (email e telefone). Antes disso, o campo de senha não é obrigatório porque o usuário ainda está na etapa de validação do email e telefone. Após a validação ser bem-sucedida, o campo de senha se torna obrigatório para garantir que o usuário forneça uma nova senha para a conta.
                      editavel={dadosValidados} // O campo de confirmação de senha é editável apenas após a validação bem-sucedida dos dados iniciais (email e telefone)
                      tamanhoMaximo={20}
                      nome="senha2"
                      id="senha2"
                      valor={senha2} 
                      aoDigitado={valor => setSenha2(valor)}
                    />
                  </div>

                  {/* Validação da sennha */}
                  {senha1.length > 0 && (
                      <ul className="col-12 col-md-6 list-unstyled mt-2 fs-4">
                          <li className={criterios.tamanho    ? 'text-success' : 'text-danger'}><i className={`bi bi-${criterios.tamanho    ? 'check' : 'x'}-circle-fill me-1`}></i>6 a 12 caracteres</li>
                          <li className={criterios.maiuscula  ? 'text-success' : 'text-danger'}><i className={`bi bi-${criterios.maiuscula  ? 'check' : 'x'}-circle-fill me-1`}></i>1 letra maiúscula</li>
                          <li className={criterios.minuscula  ? 'text-success' : 'text-danger'}><i className={`bi bi-${criterios.minuscula  ? 'check' : 'x'}-circle-fill me-1`}></i>1 letra minúscula</li>
                          <li className={criterios.numero     ? 'text-success' : 'text-danger'}><i className={`bi bi-${criterios.numero     ? 'check' : 'x'}-circle-fill me-1`}></i>1 número</li>
                          <li className={criterios.semSimbolo ? 'text-success' : 'text-danger'}><i className={`bi bi-${criterios.semSimbolo ? 'check' : 'x'}-circle-fill me-1`}></i>Sem símbolos</li>
                          <li className={criterios.senhasIguais ? 'text-success' : 'text-danger'}><i className={`bi bi-${criterios.senhasIguais ? 'check' : 'x'}-circle-fill me-1`}></i>Senhas conferem</li>
                      </ul>
                  )}

                  <CampoTexto 
                        label="Código de Ativação"
                        labelClass="form-label fs-5"
                        className="form-control form-control-lg fs-3"
                        wrapperClass="col-12 col-md-6"
                        tipo="text"
                        placeholder="Digite seu código de ativação"
                        obrigatorio={dadosValidados} // O campo de código de ativação é obrigatório após a validação bem-sucedida dos dados iniciais (email e telefone). Antes disso, o campo de código de ativação não é obrigatório porque o usuário ainda está na etapa de validação do email e telefone. Após a validação ser bem-sucedida, o campo de código de ativação se torna obrigatório para garantir que o usuário forneça um código válido para a conta.
                        editavel={dadosValidados} // Torna o campo de código de ativação editável apenas após a validação bem-sucedida dos dados iniciais (email e telefone)
                        tamanhoMaximo={11}
                        nome="codAtivacao"
                        id="codAtivacao"
                        /*O valor do campo será controlado pelo estado codAtivacao */
                        valor={codAtivacao} 
                        /*Quando o usuário digitar algo, a função setCodAtivacao será chamada para atualizar o estado*/
                        aoDigitado={valor => setCodAtivacao(valor)} 
                    />

                    <div className="form-check mb-3">
                        <input 
                            className="form-check-input"
                            type="checkbox"
                            id="checkbox"
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setDadosValidados(true); // Se o checkbox for marcado, considera os dados como validados, permitindo que os campos de senha e código de ativação sejam editáveis.
                                } else {
                                    setDadosValidados(false); // Se o checkbox for desmarcado, considera os dados como não validados, tornando os campos de senha e código de ativação somente leitura.
                                }   
                            }}
                        />

                        <label className="form-check-label fs-5" htmlFor="checkbox">
                            Já tenho o código de ativação e quero redefinir minha senha
                        </label>
                    </div>
                    

                    <Mensagem mensagem={mensagem.texto} tipo={mensagem.tipo} />

                    {/* O botão de cadastro é desativado enquanto o carregamento está em andamento */}
                    <Botao className="btn btn-primary btn-lg" tipo="submit" aoClicar={null} disabled={carregando} >
                     {carregando ? 'Validando...' : 'Enviar'}
                     
                    </Botao>
                    
                </form>
                
            </div>
        </div>
    );
}
export default RedefineSenha;